import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getDashboard } from "../api/educentialApi";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Circle,
  Download,
  AlertTriangle,
  Calendar
} from "lucide-react";

export default function ApplicationProgress() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchDashboardData = async () => {
    setLoading(true);
    setError("");
    try {
      const dashboardData = await getDashboard();
      setData(dashboardData);
    } catch (err) {
      console.error("Failed to load application progress data:", err);
      setError("Unable to sync your application timeline. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-full flex items-center justify-center px-4 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading progress...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-gray-50 min-h-full flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full text-center bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
          <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Progress</h3>
          <p className="text-gray-600 text-sm mb-6">{error || "Something went wrong."}</p>
          <button
            onClick={fetchDashboardData}
            className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold text-sm transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const applicationStages = data?.applicationStages || [];

  // Show empty state if no application stages
  if (applicationStages.length === 0) {
    return (
      <div className="bg-gray-50 min-h-full">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Link
                  to="/dashboard"
                  className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Application Progress
                  </h1>
                  <p className="text-gray-500 text-sm mt-1">
                    Track your application status and milestones
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Application Yet
              </h3>
              <p className="text-gray-600 mb-6">
                You haven't started an application yet. Begin your journey by creating an application.
              </p>
              <Link
                to="/dashboard/application"
                className="inline-block px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors"
              >
                Start Application
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Application Progress
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  Track your application status and milestones
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Timeline */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Step
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Stage
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date Completed
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {applicationStages.map((stage, idx) => (
                  <tr
                    key={stage.id}
                    className={`transition-colors ${
                      stage.status === "Completed"
                        ? "bg-green-50/30 hover:bg-green-50/50"
                        : stage.status === "In Progress"
                        ? "bg-yellow-50/30 hover:bg-yellow-50/50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-gray-500">
                        {idx + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-gray-900">
                        {stage.title}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {stage.notes || "Waiting for earlier steps to complete"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stage.completedAt ? (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{new Date(stage.completedAt).toLocaleDateString()}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Not completed</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stage.status === "Completed" ? (
                        <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 text-xs font-semibold">
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span>Completed</span>
                        </span>
                      ) : stage.status === "In Progress" ? (
                        <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs font-semibold">
                          <Clock className="w-3.5 h-3.5" />
                          <span>In Progress</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-xs font-semibold">
                          <Circle className="w-3 h-3" />
                          <span>Pending</span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stage.downloadUrl ? (
                        <a
                          href={stage.downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center space-x-1 px-3 py-1.5 bg-white hover:bg-gray-50 text-yellow-600 hover:text-yellow-700 rounded-lg border border-gray-300 transition-all text-xs font-semibold"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span>Download</span>
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden p-4 space-y-4">
            {applicationStages.map((stage, idx) => (
              <div
                key={stage.id}
                className={`p-4 rounded-lg border ${
                  stage.status === "Completed"
                    ? "bg-green-50 border-green-200"
                    : stage.status === "In Progress"
                    ? "bg-yellow-50 border-yellow-200"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <span className="text-xs font-bold text-gray-500">
                    Step {idx + 1}
                  </span>
                  {stage.status === "Completed" ? (
                    <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 text-xs font-semibold">
                      Completed
                    </span>
                  ) : stage.status === "In Progress" ? (
                    <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs font-semibold">
                      In Progress
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-xs font-semibold">
                      Pending
                    </span>
                  )}
                </div>

                <h4 className="text-base font-semibold text-gray-900 mb-1">
                  {stage.title}
                </h4>
                <p className="text-gray-600 text-sm mb-4">
                  {stage.notes || "Waiting for earlier steps to complete"}
                </p>

                <div className="flex justify-between items-center pt-3 border-t border-gray-200 text-xs">
                  <span className="text-gray-500">
                    {stage.completedAt
                      ? `Completed: ${new Date(stage.completedAt).toLocaleDateString()}`
                      : "Not completed"}
                  </span>
                  {stage.downloadUrl && (
                    <a
                      href={stage.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center space-x-1 text-yellow-600 hover:text-yellow-700 font-semibold"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Download</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
